import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Settings, ArrowLeft, Palette, FileCheck, ShieldCheck, Mail } from 'lucide-react';

function useIsMobile() {
  const [m, setM] = useState(false);
  useEffect(() => { const c = () => setM(window.innerWidth < 768); c(); window.addEventListener('resize', c); return () => window.removeEventListener('resize', c); }, []);
  return m;
}

const FEATURES = [
  { icon: Palette, title: 'Brand Customization', desc: 'Upload your logo, choose brand colors, and customize report theme', gradient: 'from-orange-500 to-amber-400', iconBg: 'bg-orange-500/10', iconColor: 'text-orange-400' },
  { icon: FileCheck, title: 'Custom Disclaimers', desc: 'Add your own disclaimers, terms, and regulatory disclosures', gradient: 'from-blue-500 to-cyan-400', iconBg: 'bg-blue-500/10', iconColor: 'text-blue-400' },
  { icon: ShieldCheck, title: 'SEBI Registration', desc: 'Add your SEBI/RIA registration number to all reports', gradient: 'from-emerald-500 to-teal-400', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-400' },
  { icon: Mail, title: 'Email Templates', desc: 'Customize email templates for sending plans to clients', gradient: 'from-purple-500 to-violet-400', iconBg: 'bg-purple-500/10', iconColor: 'text-purple-400' },
];

export default function AdvisorSettings({ user, onLogout }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  return (
    <div data-testid="advisor-settings" className="min-h-screen p-4 lg:p-8 relative overflow-hidden">
      <Navigation user={user} onLogout={onLogout} />
      {!isMobile && (
        <div className="pointer-events-none fixed inset-0 z-0" aria-hidden="true">
          <motion.div className="absolute w-[400px] h-[400px] rounded-full bg-gradient-to-r from-emerald-600/[0.06] to-teal-500/[0.06] blur-3xl" animate={{ x: [0, -20, 0], y: [0, 25, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} style={{ top: '10%', left: '5%' }} />
        </div>
      )}
      <motion.div className="max-w-7xl mx-auto relative z-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>

        <motion.div className="mb-8" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-3 mb-2">
            <motion.div className="w-2 h-8 rounded-full bg-gradient-to-b from-emerald-500 to-teal-400" initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 0.4, delay: 0.2 }} />
            <h1 data-testid="settings-header" className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
              <span className="bg-gradient-to-r from-emerald-400 to-teal-300 bg-clip-text text-transparent">Advisor</span> Settings
            </h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-5 text-sm">Customize your branding and preferences</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
          <Card data-testid="settings-coming-soon" className="p-10 border-gray-200 dark:border-gray-800/60 text-center">
            <div className="max-w-2xl mx-auto">
              <motion.div
                className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 flex items-center justify-center"
                animate={!isMobile ? { rotate: [0, 90, 0] } : {}}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              >
                <Settings className="w-7 h-7 text-emerald-400/60" />
              </motion.div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Customization Settings Coming Soon</h2>
              <p className="text-gray-500 text-sm mb-8">
                Personalize your financial planning reports with professional branding:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-8">
                {FEATURES.map((feat, idx) => (
                  <motion.div
                    key={feat.title}
                    data-testid={`settings-feature-${idx}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: isMobile ? 0 : 0.25 + idx * 0.07 }}
                    whileHover={!isMobile ? { y: -3 } : {}}
                    className="group"
                  >
                    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-800/60 hover:border-gray-300 dark:hover:border-gray-700/80 transition-all duration-300 relative overflow-hidden">
                      <div className={`absolute inset-0 bg-gradient-to-br ${feat.gradient} opacity-0 group-hover:opacity-[0.04] transition-opacity duration-500`} />
                      <div className="relative z-10 flex items-start gap-3">
                        <div className={`w-9 h-9 rounded-lg ${feat.iconBg} flex items-center justify-center flex-shrink-0`}>
                          <feat.icon className={`w-4 h-4 ${feat.iconColor}`} />
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-0.5">{feat.title}</h3>
                          <p className="text-xs text-gray-500">{feat.desc}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="flex items-center justify-center gap-3">
                <Button
                  data-testid="settings-back-btn"
                  onClick={() => navigate('/advisor/clients')}
                  variant="outline"
                  className="border-gray-700 hover:bg-gray-800/50"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Clients
                </Button>
                <Button
                  data-testid="settings-create-plan-btn"
                  onClick={() => navigate('/advisor/create-plan')}
                  className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 shadow-lg shadow-emerald-600/20"
                >
                  Create Plan
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

      </motion.div>
      <Footer />
    </div>
  );
}
